/**
 * Order ticket for one outcome (#674), outcome trading rules UI (#676), sign + submit via the user's wallet (#677).
 *
 * By default every order is signed by the connected wallet. MetaMask only shows an opaque hash for L1 actions, so
 * the "Confirm in your wallet" dialog shows the full order summary while the wallet prompt is open.
 * Opt-in 1-click trading (#688): with an approved agent the summary is confirmed in an in-app dialog and the agent
 * key signs locally (no wallet prompt, no chain switch). The mainnet gate applies the same way in both modes.
 * The wallet is NOT switched back from chain 1337 afterwards (nothing needs the original chain; see S4 notes).
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Hex } from 'viem';
import { AgentToggle } from '../agent/AgentControls';
import { builderFeeUsd, feeToPercent, formatFeeUsd, isBuilderApprovalError } from '../builder/builder';
import { BuilderFeeApproval } from '../builder/BuilderFeeApproval';
import { useBuilder } from '../builder/useBuilder';
import { useAgent } from '../agent/context';
import { browseOnlyMessage, useGeo } from '../geo/context';
import { useBalances } from '../account/useBalances';
import { useUserSession } from '../account/UserSession';
import { orderEvents } from '../account/orders/orderEvents';
import { orderLifecycle } from '../account/orders/lifecycle';
import Modal from '../components/Modal';
import { toasts } from '../components/toastStore';
import { useEnv } from '../config/env';
import { coin as outcomeCoin, type Side } from '../hl/outcomes';
import { useHlHealth, useSubscription, useWsStatus } from '../hl/react';
import { outcomeMid, useAllMids } from '../hl/mids';
import type { OrderAction } from '../hl/signing';
import type { L2Book } from '../hl/types';
import { formatShares, formatUsd, pxToCents } from '../market/format';
import { takeCloseRequest, useCloseRequest, useTicketPrefill } from '../market/ticketPrefill';
import { allowedActions, availableUsdc, outcomeBalances, outcomeHoldings } from '../trading/rules';
import { useViewedAccount, useWallet } from '../wallet/context';
import { useOutcomeMeta } from '../account/useMarketData';
import { truncateTitle } from '../describe/format';
import { useDescriber } from '../describe/useDescribe';
import { outcomeInVenue, venueIndex, venueLabel } from '../venue/scope';
import { useVenueScope } from '../venue/useVenueScope';
import {
  buildMarketOrderAction,
  buildOutcomeOrderAction,
  centsToPx,
  checkMarketTicket,
  checkTicket,
  economics,
  isMainnetConfirmed,
  mainnetTradingEnabled,
  submitGate,
  markMainnetConfirmed,
  pxToCentsInput,
  signAndSubmitOrder,
  TIF_OPTIONS,
  TICKET_TIF_ORDER,
  DEFAULT_TICKET_TIF,
  bookClickOrder,
  type TicketTif,
} from './order';
import { applyOrderUpdate, statusDetail, type EntryState } from './results';
import { WalletConfirmModal } from './WalletConfirm';
import { walletProfile } from '../wallet/compat';
import { t as tr } from '../i18n/i18n';
import { delocalizeInput, localizeNumber, percent } from '../i18n/format';
import { healthText, networkWord } from '../i18n/common';
import { useT } from '../i18n/useT';

export interface OrderTicketProps {
  outcome: number;
  sideNames: readonly [string, string];
  /** Page side (Yes/No), kept in sync both ways. */
  side: Side;
  onSideChange: (s: Side) => void;
  /** #2242: inline on Markets, the panel's close button sits in the ticket's top row, after "How does this work?". */
  onClose?: () => void;
}

interface Summary {
  outcome: number;
  /** Readable market title from describe/ ("BTC above $81,300 at Oct 17, 04:00 UTC?"); "Outcome #id" until meta loads. */
  title: string;
  side: Side;
  sideName: string;
  isBuy: boolean;
  /** Wire price: the limit price, or for Market the price bound (worst price). */
  px: string;
  sz: string;
  tif: TicketTif;
  /** Limit: px * sz. Market: worst case, boundPx * sz. */
  notional: string;
  /** Market only: estimated average fill price from the book when submitted (null: nothing to fill against). */
  estAvgPx?: string | null;
  /** Market only: estimated USDC paid / received at the book when submitted (null: nothing to fill against). */
  estNotional?: string | null;
  /** Builder fee (#770) in USDC on this order (estimated for Market); absent when builder codes are off. */
  builderFee?: string;
  /** Builder fee rate in percent ("0.01"). */
  builderPct?: string;
}

type Phase =
  | { step: 'idle' }
  | { step: 'mainnet-confirm'; summary: Summary; action: OrderAction }
  | { step: 'agent-confirm'; summary: Summary; action: OrderAction }
  | { step: 'signing'; summary: Summary }
  | { step: 'submitting'; summary: Summary };

interface Entry {
  id: number;
  summary: Summary;
  state: EntryState;
  oid?: number;
  detail: string;
}

const usd = formatUsd;

function summaryText(s: Summary): string {
  const at =
    s.tif === 'Market'
      ? tr('ticket.summary_at_market', { px: pxToCents(s.px) })
      : tr('ticket.summary_at_limit', { px: pxToCents(s.px), tif: s.tif });
  return tr('ticket.summary_line', {
    action: tr(s.isBuy ? 'ticket.buy' : 'ticket.sell'),
    sz: localizeNumber(s.sz),
    side: s.sideName,
    at,
    title: truncateTitle(s.title),
  });
}

const outcomeFallbackTitle = (outcome: number) => `Outcome #${outcome}`;

const STATE_CLS: Record<string, string> = {
  open: 'text-fg', // #1546: not the accent, which is the same purple as No / sell
  filled: 'text-yes',
  canceled: 'text-muted',
  rejected: 'text-danger',
  error: 'text-danger',
};

function SummaryList({ s, envName }: { s: Summary; envName: string }) {
  const t = useT();
  return (
    <dl className="grid grid-cols-2 gap-y-1 text-sm" data-testid="order-summary">
      <dt className="text-muted">{t('ticket.summary.network')}</dt>
      <dd className="text-end font-medium">{networkWord(envName)}</dd>
      <dt className="text-muted">{t('common.market')}</dt>
      <dd className="text-end" data-testid="order-summary-market">
        <span className="font-medium">{s.title}</span>{' '}
        {s.title !== outcomeFallbackTitle(s.outcome) && <span className="text-muted">(#{s.outcome})</span>}
      </dd>
      <dt className="text-muted">{t('ticket.summary.action')}</dt>
      <dd className={`text-end font-semibold ${s.isBuy ? 'text-yes' : 'text-no'}`}>
        {t('ticket.submit', { action: t(s.isBuy ? 'ticket.buy' : 'ticket.sell'), side: s.sideName })}
      </dd>
      <dt className="text-muted">{t('common.shares')}</dt>
      <dd className="num text-end">{localizeNumber(s.sz)}</dd>
      {s.tif === 'Market' && (
        <>
          <dt className="text-muted">{t('ticket.est_avg_price')}</dt>
          <dd className="num text-end" data-testid="order-summary-est">
            {s.estAvgPx ? pxToCents(s.estAvgPx) : '—'}
          </dd>
          <dt className="text-muted">{t(s.isBuy ? 'ticket.est_cost' : 'ticket.est_proceeds')}</dt>
          <dd className="num text-end" data-testid="order-summary-est-notional">
            {s.estNotional ? t('ticket.summary.amount_usdc', { amount: usd(s.estNotional) }) : '—'}
          </dd>
        </>
      )}
      <dt className="text-muted">{t(s.tif === 'Market' ? 'ticket.worst_price' : 'ticket.limit_price_row')}</dt>
      <dd className="num text-end">
        {pxToCents(s.px)} <span className="text-muted">({s.px})</span>
      </dd>
      <dt className="text-muted">{t('ticket.tif')}</dt>
      <dd className="text-end">{s.tif === 'Market' ? t('ticket.tif.market_ioc') : s.tif}</dd>
      {/* Market: the worst case is at the price bound (a sell at 0.00001 shows ~$0), so it is labelled as such and
          printed with 5 decimals; the estimate above is what the book says the order should trade at (#749). */}
      <dt className="text-muted">
        {t(
          s.tif === 'Market'
            ? s.isBuy
              ? 'ticket.worst_cost'
              : 'ticket.worst_proceeds'
            : s.isBuy
              ? 'ticket.summary.max_cost'
              : 'ticket.summary.min_proceeds',
        )}
      </dt>
      <dd className="num text-end" data-testid="order-summary-notional">
        {t('ticket.summary.amount_usdc', { amount: usd(s.notional, s.tif === 'Market' && !s.isBuy ? 5 : 2) })}
      </dd>
      {s.builderFee !== undefined && (
        <>
          <dt className="text-muted">
            {t('ticket.outfox_fee')} <span className="text-[11px]">({percent(s.builderPct ?? '')})</span>
          </dt>
          <dd className="num text-end" data-testid="order-summary-builder-fee">
            {s.tif === 'Market' ? '≈ ' : ''}
            {t('ticket.summary.amount_usdc', { amount: formatFeeUsd(s.builderFee) })}
          </dd>
        </>
      )}
    </dl>
  );
}

export default function OrderTicket({ outcome, sideNames, side, onSideChange, onClose }: OrderTicketProps) {
  const t = useT();
  const { env } = useEnv();
  // #2242: the Yes / No buttons show each side's mid in cents (shared allMids cache: no extra request).
  const { mids } = useAllMids();
  // #771: warn while Hyperliquid is degraded, refuse to submit while it is down.
  const health = useHlHealth();
  // #769: browse-only jurisdictions see the ticket but cannot submit.
  const geo = useGeo();
  const wallet = useWallet();
  const agent = useAgent();
  const viewed = useViewedAccount();
  const ownAddress = wallet.status === 'connected' ? wallet.address : null;
  const tradeAddress = viewed.canTrade ? ownAddress : null;
  // Held by the user session (#1809). Nothing below acts on balances that are not known to be current.
  const balances = useBalances(tradeAddress);
  const session = useUserSession();
  const balancesStale = tradeAddress !== null && balances.stale;
  const balancesStaleRef = useRef(balancesStale);
  balancesStaleRef.current = balancesStale;
  const socketUp = useWsStatus('user') === 'open';
  // #770 builder code: off unless configured (then every order carries {b, f} and the fee must be approved once).
  const builder = useBuilder(tradeAddress);
  const builderPct = builder.cfg ? feeToPercent(builder.cfg.fee) : null;
  // #689 defence in depth: never sign an order for an outcome outside the venue scope (the market page already
  // refuses to render one). Unknown venue (meta loading / outcome not listed) also blocks.
  const { venue: scopeVenue } = useVenueScope();
  // Always loaded (the market page has it cached): the order summary names the market by its readable title.
  const scopeMeta = useOutcomeMeta();
  const describer = useDescriber(scopeMeta);
  const marketTitle = useMemo(() => describer.byId(outcome)?.title ?? outcomeFallbackTitle(outcome), [describer, outcome]);
  const venueOk = useMemo(
    () => (scopeVenue === null ? true : outcomeInVenue(venueIndex(scopeMeta?.outcomes), outcome, scopeVenue)),
    [scopeVenue, scopeMeta, outcome],
  );

  const [isBuy, setIsBuy] = useState(true);
  const [cents, setCents] = useState('');
  const [sz, setSz] = useState('');
  const [tif, setTif] = useState<TicketTif>(DEFAULT_TICKET_TIF);
  const isMarket = tif === 'Market';
  const [phase, setPhase] = useState<Phase>({ step: 'idle' });
  const [notice, setNotice] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const entryId = useRef(0);

  const rows = tradeAddress ? (balances.data?.balances ?? null) : null;
  const holdings = useMemo(() => (rows ? outcomeHoldings(rows, outcome) : { holdYes: '0', holdNo: '0' }), [rows, outcome]);
  const sideBal = useMemo(() => (rows ? outcomeBalances(rows, outcome) : null), [rows, outcome]);
  const usdc = rows ? availableUsdc(rows) : undefined;
  const allowed = allowedActions(holdings);
  const canSellSide = side === 0 ? allowed.sellYes : allowed.sellNo;
  const maxSell = side === 0 ? allowed.maxSellYes : allowed.maxSellNo;
  const sideLabel = sideNames[side];

  // Prefill from a book click (ticketPrefill.ts): the clicked price, and side + TIF by bookClickOrder (#2242): an ask is
  // a buy IOC; a bid is a sell IOC of held shares, or with none held a resting buy (GTC) at that bid.
  const prefill = useTicketPrefill();
  const lastSeq = useRef<number | null>(null);
  const allowedRef = useRef(allowed);
  allowedRef.current = allowed;
  useEffect(() => {
    if (!prefill || prefill.outcome !== outcome || prefill.seq === lastSeq.current) return;
    lastSeq.current = prefill.seq;
    if (prefill.side !== side) onSideChange(prefill.side);
    setCents(pxToCentsInput(prefill.px));
    const a = allowedRef.current;
    const next = bookClickOrder(prefill.isBuy, prefill.side === 0 ? a.sellYes : a.sellNo);
    setTif(next.tif);
    setIsBuy(next.isBuy);
    setNotice(null);
  }, [prefill, outcome, side, onSideChange]);

  // #873 "Close" from the portfolio: once the own balances are loaded, fill in a Market SELL of the whole free
  // position (shares minus those reserved by resting sells) on the side held. The user still reviews and signs:
  // nothing is submitted here, and the mainnet gate / confirm steps apply as for any order.
  const closeReq = useCloseRequest();
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!closeReq || closeReq.outcome !== outcome || !rows || balancesStale) return;
    const req = takeCloseRequest(outcome);
    if (!req) return;
    if (req.side !== side) onSideChange(req.side);
    const bal = outcomeBalances(rows, outcome);
    const sb = req.side === 0 ? bal.yes : bal.no;
    const a = allowedActions(outcomeHoldings(rows, outcome));
    const free = req.side === 0 ? a.maxSellYes : a.maxSellNo;
    const name = sideNames[req.side];
    // On a phone the ticket sits below the book: bring it into view.
    sectionRef.current?.scrollIntoView?.({ block: 'start' });
    setTif('Market');
    if (Number(sb.total) <= 0) {
      setIsBuy(true);
      setSz('');
      setNotice(tr('positions.close.nothing', { side: name }));
    } else if (free <= 0) {
      setIsBuy(true);
      setSz('');
      setNotice(tr('positions.close.all_reserved', { total: formatShares(sb.total), side: name }));
    } else {
      setIsBuy(false);
      setSz(String(free));
      setNotice(
        Number(sb.hold) > 0
          ? tr('positions.close.reserved', {
              reserved: formatShares(sb.hold),
              total: formatShares(sb.total),
              side: name,
              free: formatShares(free),
            })
          : tr('positions.close.ready', { n: formatShares(free), side: name }),
      );
    }
  }, [closeReq, outcome, rows, balancesStale, side, onSideChange, sideNames]);

  // A sell can never exceed what is held: flip back to Buy if the holdings for this side disappear.
  useEffect(() => {
    if (!isBuy && rows && !canSellSide) setIsBuy(true);
  }, [isBuy, rows, canSellSide]);

  // Live status for submitted orders. Never loses the fill size / price from the submit response (#748).
  useEffect(() => {
    if (!tradeAddress) return;
    const me = tradeAddress.toLowerCase();
    return orderEvents.subscribe((e) => {
      if (e.user !== me) return;
      setEntries((prev) => {
        const next = prev.map((en) => applyOrderUpdate(en, e.update));
        return next.some((en, i) => en !== prev[i]) ? next : prev;
      });
    });
  }, [tradeAddress]);

  // Market (#746): the book of the ticket's side, for the estimated fill. Shared with OrderBook's subscription
  // (ref-counted, the last frame goes to this late listener).
  const bookCoin = outcomeCoin(outcome, side);
  const [latestBook, setLatestBook] = useState<L2Book | null>(null);
  useSubscription(isMarket ? { type: 'l2Book' as const, coin: bookCoin } : null, (data) => {
    if (data.coin === bookCoin) setLatestBook(data);
  });
  const book = latestBook?.coin === bookCoin ? latestBook : null;

  const px = centsToPx(cents);
  const ticketBase = { side, isBuy, sz, holdings, usdcAvailable: usdc, sideLabels: sideNames };
  const market = isMarket ? checkMarketTicket({ ...ticketBase, book }) : null;
  const check = market ?? checkTicket({ ...ticketBase, px });
  const econ = market ? economics(market.estimate?.avgPx ?? null, sz, isBuy) : economics(px, sz, isBuy);
  // Builder fee on what the order trades: limit = px * sz; Market = the estimated fill (econ uses the est. avg price).
  const builderFee = builder.cfg && econ ? builderFeeUsd(econ.notional, builder.cfg.fee) : null;
  const builderBlocks = builder.approval === 'needed';
  const touched = sz !== '' || (!isMarket && cents !== '');
  const busy = phase.step !== 'idle';

  const addEntry = (summary: Summary, st: { state: EntryState; oid?: number; detail: string }) =>
    setEntries((prev) => [{ id: ++entryId.current, summary, ...st }, ...prev].slice(0, 6));

  const sign = async (summary: Summary, action: OrderAction) => {
    if (!wallet.provider || !ownAddress || venueOk !== true) return;
    // Balances can stop being current between the review and this click.
    if (balancesStaleRef.current) {
      setNotice(tr('ticket.balances_stale'));
      return;
    }
    setNotice(null);
    const signer = agent.signer;
    setPhase({ step: signer ? 'submitting' : 'signing', summary });
    // HL may echo a rejection on WS orderUpdates as well: the tracker leaves it to this response (#747).
    const settle = orderLifecycle.noteSubmitting(ownAddress, {
      coin: outcomeCoin(summary.outcome, summary.side),
      isBuy: summary.isBuy,
      px: summary.px,
      sz: summary.sz,
    });
    const res = await signAndSubmitOrder(action, {
      provider: wallet.provider,
      account: ownAddress as Hex,
      env,
      walletRdns: wallet.walletInfo?.rdns ?? null,
      agent: signer,
      onAgentMissing: () => {
        // Replaced from another device / expired / pruned: forget it and let the wallet sign this order.
        agent.forget(tr('ticket.agent_gone'));
        setPhase({ step: 'signing', summary });
      },
    }).finally(() => {
      settle();
      setPhase({ step: 'idle' });
    });
    if (res.kind === 'rejected') {
      setNotice(`${tr('ticket.signature_canceled')}${res.hint ? ` ${res.hint}` : ''}`);
      return;
    }
    const user = ownAddress.toLowerCase();
    const label = summaryText(summary);
    if ((res.kind === 'err' && isBuilderApprovalError(res.message)) ||
      (res.kind === 'ok' && res.statuses.some((st) => st.kind === 'error' && isBuilderApprovalError(st.message)))) {
      // Approval missing / withdrawn elsewhere: re-read it so the ticket asks for it again.
      builder.recheck();
    }
    if (res.kind === 'err') {
      addEntry(summary, { state: 'error', detail: res.message });
      toasts.push({ tone: 'error', title: tr('ticket.order_failed'), body: `${label} · ${res.message}` });
      return;
    }
    // Held shares / USDC update by themselves from the WS spotState stream; with no socket, the session reads them.
    session?.afterOrder();
    for (const st of res.statuses) {
      addEntry(summary, statusDetail(st));
      // Link the oid to later orderUpdates / fills for the lifecycle toasts (#679).
      if (st.kind === 'resting') orderLifecycle.noteSubmitted(user, st.oid, { label, origSz: summary.sz });
      else if (st.kind === 'filled') {
        orderLifecycle.noteImmediateFill(user, st.oid, {
          label,
          totalSz: st.totalSz,
          avgPx: st.avgPx,
          coin: outcomeCoin(summary.outcome, summary.side),
        });
      } else orderLifecycle.noteSubmitError(user, label, st.message);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!check.result.ok || (!market && px === null) || !viewed.canTrade || busy || venueOk !== true) return;
    if (health === 'down' || builderBlocks || balancesStale) return;
    if (!geo.canTrade) return;
    const gate = submitGate(env.isMainnet, mainnetTradingEnabled(), isMainnetConfirmed());
    if (gate === 'mainnet-off') return;
    let action: OrderAction;
    try {
      action = market
        ? buildMarketOrderAction({ outcome, side, isBuy, sz: sz.trim(), builder: builder.field })
        : buildOutcomeOrderAction({
            outcome,
            side,
            isBuy,
            px: px as string,
            sz: sz.trim(),
            tif: tif as Exclude<TicketTif, 'Market'>,
            builder: builder.field,
          });
    } catch (err) {
      setNotice(err instanceof Error ? err.message : String(err));
      return;
    }
    const w = action.orders[0]!;
    const summary: Summary = {
      outcome,
      title: marketTitle,
      side,
      sideName: sideLabel,
      isBuy,
      px: w.p,
      sz: w.s,
      tif,
      notional: (market ? market.worstNotional : econ?.notional) ?? '0',
      ...(market
        ? {
            estAvgPx: market.estimate?.avgPx ?? null,
            estNotional: market.estimate?.avgPx ? market.estimate.notional : null,
          }
        : {}),
    };
    if (builder.cfg && builderPct !== null) {
      // Market: on the estimated fill, else (nothing to estimate) on the worst case.
      const base = market ? (market.estimate?.avgPx ? market.estimate.notional : market.worstNotional) : econ?.notional;
      summary.builderFee = builderFeeUsd(base ?? '0', builder.cfg.fee);
      summary.builderPct = builderPct;
    }
    if (gate === 'mainnet-confirm') {
      setPhase({ step: 'mainnet-confirm', summary, action });
      return;
    }
    if (agent.signer) {
      // 1-click mode: no wallet prompt, so the summary is confirmed here.
      setPhase({ step: 'agent-confirm', summary, action });
      return;
    }
    void sign(summary, action);
  };

  // Segmented buttons: at least 40px tall on phones (tap targets), compact from sm.
  const SEG = 'min-h-10 rounded-md py-1.5 font-semibold sm:min-h-0';

  const sideBtn = (s: Side) => {
    const mid = outcomeMid(mids, outcome, s);
    return (
      <button
        key={s}
        type="button"
        role="radio"
        aria-checked={side === s}
        onClick={() => onSideChange(s)}
        className={`flex min-w-0 flex-1 items-center justify-center px-2 text-sm ${SEG} ${
          side === s ? (s === 0 ? 'bg-yes/20 text-yes' : 'bg-no/20 text-no') : 'text-muted hover:text-fg'
        }`}
      >
        <span className="truncate" translate="no">
          {sideNames[s]}
        </span>
        {/* #2242: the side's mid in cents, as the Yes / No pair beside the book used to show. */}
        {mid !== undefined && (
          <span className="num ms-1.5 shrink-0 font-normal opacity-80" data-testid={`ticket-side-mid-${s}`}>
            {pxToCents(mid)}
          </span>
        )}
      </button>
    );
  };

  const actionBtn = (buy: boolean) => {
    const disabled = !buy && !canSellSide;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={isBuy === buy}
        disabled={disabled}
        title={disabled ? t('ticket.sell_disabled', { side: sideLabel }) : undefined}
        onClick={() => setIsBuy(buy)}
        className={`flex-1 px-2 text-sm ${SEG} disabled:cursor-not-allowed disabled:opacity-40 ${
          isBuy === buy ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg'
        }`}
      >
        {t(buy ? 'ticket.buy' : 'ticket.sell')}
      </button>
    );
  };

  const tifHelp = TIF_OPTIONS.find((t) => t.value === tif)?.help;
  const tifBtn = (v: TicketTif) => {
    const o = TIF_OPTIONS.find((x) => x.value === v)!;
    return (
      <button
        key={v}
        type="button"
        role="radio"
        aria-checked={tif === v}
        title={o.help}
        onClick={() => setTif(v)}
        data-testid={`tif-${v.toLowerCase()}`}
        className={`flex-auto whitespace-nowrap px-1.5 ${SEG} text-[13px] ${tif === v ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg'}`}
      >
        {o.label}
      </button>
    );
  };
  const hasMessages = (touched && check.errors.length > 0) || check.warnings.length > 0 || notice !== null;

  let submitArea;
  if (!geo.canTrade) {
    submitArea = (
      <p role="status" className="rounded-md bg-surface-2 px-3 py-2 text-xs text-muted" data-testid="ticket-geo">
        {browseOnlyMessage(geo.decision)}
      </p>
    );
  } else if (venueOk !== true) {
    submitArea = (
      <p className="rounded-md bg-surface-2 px-3 py-2 text-xs text-muted" data-testid="ticket-out-of-venue">
        {venueOk === 'unknown' && !scopeMeta
          ? t('ticket.checking_venue')
          : t('ticket.out_of_venue', { outcome: String(outcome), venue: venueLabel(scopeVenue) })}
      </p>
    );
  } else if (env.isMainnet && !mainnetTradingEnabled()) {
    submitArea = (
      <p className="rounded-md bg-surface-2 px-3 py-2 text-xs text-muted" data-testid="ticket-mainnet-off">
        {t('ticket.mainnet_off')}
      </p>
    );
  } else if (viewed.mode === 'ghost') {
    submitArea = (
      <p className="rounded-md bg-surface-2 px-3 py-2 text-xs text-muted" data-testid="ticket-ghost">
        {t('ticket.ghost')}
      </p>
    );
  } else if (!ownAddress) {
    submitArea = (
      <button
        type="button"
        onClick={() => void wallet.connect()}
        className="w-full rounded-lg bg-fg px-3 py-2 text-sm font-semibold text-bg"
      >
        {t('ticket.connect_to_trade')}
      </button>
    );
  } else {
    const profile = walletProfile(wallet.walletInfo?.rdns ?? null, wallet.provider);
    submitArea = (
      <>
        <div className="mb-2">
          <AgentToggle variant="ticket" />
        </div>
        {profile.support === 'unsupported' && agent.status !== 'on' && (
          <p className="mb-2 rounded-md bg-danger/10 px-3 py-2 text-xs text-danger" data-testid="ticket-wallet-unsupported">
            {profile.note}
          </p>
        )}
        <BuilderFeeApproval builder={builder} />
        {health !== 'ok' && (
          <p
            role={health === 'down' ? 'alert' : 'status'}
            data-testid="ticket-health"
            data-status={health}
            className={`mb-2 rounded-md px-3 py-2 text-xs ${health === 'down' ? 'bg-danger/10 text-danger' : 'bg-warn/10 text-warn'}`}
          >
            {healthText(health, env.name)}
          </p>
        )}
        {balancesStale && !socketUp && (
          <p role="status" data-testid="ticket-balances-stale" className="mb-2 rounded-md bg-warn/10 px-3 py-2 text-xs text-warn">
            {t('ticket.balances_stale')}
          </p>
        )}
      <button
        type="submit"
        disabled={check.errors.length > 0 || busy || health === 'down' || builderBlocks || balancesStale}
        title={
          health === 'down'
            ? t('ticket.down_title', { network: networkWord(env.name) })
            : builderBlocks
              ? t('ticket.approve_fee_first')
              : balancesStale
                ? t('ticket.balances_stale')
                : undefined
        }
        className={`w-full rounded-lg px-3 py-2 text-sm font-semibold text-bg disabled:opacity-50 ${
          isBuy ? 'bg-yes' : 'bg-no'
        }`}
      >
        {phase.step === 'submitting'
          ? t('ticket.placing')
          : t('ticket.submit', { action: t(isBuy ? 'ticket.buy' : 'ticket.sell'), side: sideLabel })}
      </button>
      </>
    );
  }

  return (
    <section ref={sectionRef} aria-label={t('ticket.title')} className="card space-y-3 p-4" data-tour="ticket">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{t('ticket.title')}</h2>
        <div className="flex items-center gap-1">
          <Link to="/learn#trading" className="text-[11px] text-accent hover:underline" data-testid="ticket-learn-link">
            {t('ticket.learn_link')}
          </Link>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              title={t('common.close')}
              data-testid="market-panel-close"
              className="-my-1 -me-2 rounded-md px-2 py-1 text-muted hover:bg-surface-2 hover:text-fg"
            >
              ✕
            </button>
          ) : null}
        </div>
      </div>
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <div role="radiogroup" aria-label={t('ticket.side_group')} className="flex rounded-lg bg-surface-2/50 p-1">
          {sideBtn(0)}
          {sideBtn(1)}
        </div>
        <div role="radiogroup" aria-label={t('ticket.action_group')} className="flex rounded-lg bg-surface-2/50 p-1" data-tour="ticket-sides">
          {actionBtn(true)}
          {actionBtn(false)}
        </div>

        {/* #2242: time in force as one row of buttons, above price and shares. Market is the default; the help of the
            selected one is the buttons' tooltip and their accessible description (not a line of its own, to save room). */}
        <div
          role="radiogroup"
          aria-label={t('ticket.tif')}
          aria-describedby="ticket-tif-help"
          className="flex gap-0.5 rounded-lg bg-surface-2/50 p-1"
          data-tour="ticket-price"
        >
          {TICKET_TIF_ORDER.map(tifBtn)}
        </div>
        <span id="ticket-tif-help" className="sr-only" data-testid="tif-help">
          {tifHelp}
        </span>

        {/* Market (#746) is sent as IOC at the price bound: no price to enter, so the box is disabled (it stays, so the
            ticket does not change height between order types). The estimated cost is under Est. cost below. */}
        <label className="block text-xs text-muted">
          <span className="flex items-baseline justify-between gap-2">
            <span>{t('ticket.limit_price')}</span>
            <span
              className={`num truncate text-[11px] ${!isMarket && px !== null ? '' : 'invisible'}`}
              aria-hidden={isMarket || px === null ? true : undefined}
            >
              {px !== null ? t('ticket.per_share', { px: localizeNumber(px) }) : ' '}
            </span>
          </span>
          <div className={`mt-1 flex items-center rounded-md border border-border px-2 ${isMarket ? 'bg-surface-2/50' : 'bg-bg'}`}>
            <input
              aria-label={t('ticket.limit_price_aria')}
              inputMode="decimal"
              autoComplete="off"
              disabled={isMarket}
              value={isMarket ? '' : cents}
              onChange={(e) => setCents(delocalizeInput(e.target.value))}
              placeholder={isMarket ? t('ticket.tif.market') : t('ticket.limit_price_placeholder', { example: localizeNumber('63.5') })}
              className="num w-full bg-transparent py-1.5 text-sm text-fg outline-none disabled:cursor-not-allowed"
            />
            <span className="text-sm text-muted">¢</span>
          </div>
        </label>

        <label className="block text-xs text-muted">
          {t('common.shares')}
          <div className="mt-1 flex items-center gap-2 rounded-md border border-border bg-bg px-2">
            <input
              aria-label={t('common.shares')}
              inputMode="numeric"
              autoComplete="off"
              value={sz}
              onChange={(e) => setSz(e.target.value)}
              placeholder="0"
              className="num w-full bg-transparent py-1.5 text-sm text-fg outline-none"
            />
            {!isBuy && maxSell > 0 && (
              <button type="button" className="shrink-0 text-xs text-accent" onClick={() => setSz(String(maxSell))}>
                {t('ticket.max', { n: localizeNumber(String(maxSell)) })}
              </button>
            )}
          </div>
        </label>

        {/* Same height for Buy and Sell (#2242). Sells only close shares you hold (#707). This is enforced by the
            ticket, not by a reduce-only flag: Hyperliquid rejects reduce-only outcome orders ("Reduce-only is invalid
            for spot trading.", #745). */}
        <dl className="grid grid-cols-2 gap-y-1 rounded-md bg-surface-2/50 p-2 text-xs" data-testid="ticket-economics" data-tour="ticket-economics">
          {isBuy ? (
            <>
              <dt className="text-muted">{t('ticket.est_cost')}</dt>
              <dd className="num text-end">{econ ? usd(econ.notional) : '—'}</dd>
              <dt className="text-muted">{t('ticket.max_payout')}</dt>
              <dd className="num text-end">{econ?.maxPayout ? usd(econ.maxPayout) : '—'}</dd>
              <dt className="text-muted">{t('ticket.potential_profit')}</dt>
              <dd className="num text-end text-yes">{econ?.profit ? usd(econ.profit) : '—'}</dd>
            </>
          ) : (
            <>
              <dt className="text-muted">{t('ticket.est_proceeds')}</dt>
              <dd className="num text-end">{econ ? usd(econ.notional) : '—'}</dd>
              <dd className="col-span-2 line-clamp-2 h-9 text-[11px] leading-[18px] text-muted" data-testid="reduce-only">
                {t('ticket.reduce_only')}
              </dd>
            </>
          )}
          {builderPct !== null && (
            <>
              <dt className="text-muted">
                {t('ticket.outfox_fee')} <span className="text-[11px]">({percent(builderPct)})</span>
              </dt>
              <dd className="num text-end" data-testid="ticket-builder-fee">
                {builderFee !== null ? `${isMarket ? '≈ ' : ''}${formatFeeUsd(builderFee)}` : '—'}
              </dd>
            </>
          )}
          <dt className="text-muted">{t('ticket.available_usdc')}</dt>
          <dd className="num text-end" data-testid="ticket-usdc">
            {usdc !== undefined ? usd(usdc) : '—'}
          </dd>
          <dt className="text-muted">{t('ticket.held', { first: sideNames[0], second: sideNames[1] })}</dt>
          <dd className="num text-end" data-testid="ticket-held">
            {sideBal ? `${formatShares(sideBal.yes.available)} / ${formatShares(sideBal.no.available)}` : '—'}
          </dd>
        </dl>

        {/* Errors, warnings and notices in a box of fixed height (#2242), scrolling when there are many, so the ticket
            keeps its size while the user types or switches order type. */}
        <div className="h-8 space-y-0.5 overflow-y-auto" data-testid="ticket-messages" tabIndex={hasMessages ? 0 : undefined}>
          {touched && check.errors.length > 0 && (
            <ul className="space-y-0.5 text-xs text-danger" data-testid="ticket-errors">
              {check.errors.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
          {check.warnings.length > 0 && (
            <ul className="space-y-0.5 text-xs text-warn" data-testid="ticket-warnings">
              {check.warnings.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
          {notice && (
            <p role="status" className="text-xs text-warn" data-testid="ticket-notice">
              {notice}
            </p>
          )}
        </div>

        {submitArea}
      </form>

      {entries.length > 0 && (
        <ul aria-label={t('ticket.results')} aria-live="polite" className="space-y-1.5 border-t border-border pt-3">
          {entries.map((en) => (
            <li key={en.id} className="text-xs" data-testid="order-result" data-state={en.state}>
              <div className="font-medium">{summaryText(en.summary)}</div>
              <div className={STATE_CLS[en.state] ?? 'text-muted'}>{en.detail}</div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={phase.step === 'mainnet-confirm'}
        onClose={() => setPhase({ step: 'idle' })}
        title={t('ticket.mainnet_title')}
      >
        {phase.step === 'mainnet-confirm' && (
          <div className="space-y-3">
            <p className="rounded-md bg-warn px-3 py-2 text-xs font-semibold text-warn-fg">
              {t('ticket.mainnet_warning')}
            </p>
            <SummaryList s={phase.summary} envName={env.name} />
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-lg border border-border px-3 py-2 text-sm"
                onClick={() => setPhase({ step: 'idle' })}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="flex-1 rounded-lg bg-fg px-3 py-2 text-sm font-semibold text-bg"
                onClick={() => {
                  markMainnetConfirmed();
                  void sign(phase.summary, phase.action);
                }}
              >
                {t(agent.signer ? 'ticket.place_order' : 'ticket.continue_wallet')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={phase.step === 'agent-confirm'} onClose={() => setPhase({ step: 'idle' })} title={t('ticket.agent_title')}>
        {phase.step === 'agent-confirm' && (
          <div className="space-y-3" data-testid="agent-confirm">
            <SummaryList s={phase.summary} envName={env.name} />
            <p className="text-xs text-muted">
              {t('ticket.agent_body')}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-lg border border-border px-3 py-2 text-sm"
                onClick={() => setPhase({ step: 'idle' })}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="flex-1 rounded-lg bg-fg px-3 py-2 text-sm font-semibold text-bg"
                onClick={() => void sign(phase.summary, phase.action)}
              >
                {t('ticket.place_order')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <WalletConfirmModal open={phase.step === 'signing'} what="order">
        {phase.step === 'signing' && <SummaryList s={phase.summary} envName={env.name} />}
      </WalletConfirmModal>
    </section>
  );
}
